import React, { useRef, useState } from "react";
import { motion } from "framer-motion";

/**
 * Draggable before/after image comparison slider.
 * Pure pointer-events; no external libs.
 */
export const BeforeAfterSlider = ({
  before,
  after,
  beforeLabel = "Before",
  afterLabel = "After",
}) => {
  const wrapRef = useRef(null);
  const [pos, setPos] = useState(50);
  const draggingRef = useRef(false);

  const update = (clientX) => {
    const el = wrapRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const p = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.max(0, Math.min(100, p)));
  };

  const onPointerDown = (e) => {
    draggingRef.current = true;
    update(e.clientX);
  };
  const onPointerMove = (e) => {
    if (!draggingRef.current) return;
    update(e.clientX);
  };
  const onPointerUp = () => {
    draggingRef.current = false;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.7 }}
      ref={wrapRef}
      data-testid="ba-slider"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerLeave={onPointerUp}
      className="relative w-full aspect-[16/9] rounded-3xl overflow-hidden select-none cursor-ew-resize border border-[#222]"
    >
      {/* Before (full background) */}
      <img
        src={before}
        alt={beforeLabel}
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover"
        draggable={false}
      />
      <span className="absolute top-4 left-4 glass rounded-full px-3 py-1 text-[10px] tracking-[0.2em] uppercase text-[#B0B0B0]">
        {beforeLabel}
      </span>

      {/* After (clipped from left) */}
      <div
        className="absolute inset-0 overflow-hidden"
        style={{ width: `${pos}%` }}
      >
        <img
          src={after}
          alt={afterLabel}
          loading="lazy"
          className="absolute inset-0 h-full object-cover"
          style={{ width: wrapRef.current?.clientWidth || "100%", maxWidth: "none" }}
          draggable={false}
        />
        <span className="absolute top-4 left-4 glass-gold rounded-full px-3 py-1 text-[10px] tracking-[0.2em] uppercase text-[#D4AF37]">
          {afterLabel}
        </span>
      </div>

      {/* Handle */}
      <div
        className="absolute top-0 bottom-0 w-px bg-[#D4AF37] shadow-[0_0_20px_rgba(212,175,55,0.6)] pointer-events-none"
        style={{ left: `${pos}%` }}
      />
      <div
        className="absolute top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-[#D4AF37] border-4 border-black flex items-center justify-center shadow-[0_0_25px_rgba(212,175,55,0.5)] pointer-events-none"
        style={{ left: `calc(${pos}% - 20px)` }}
      >
        <span className="text-black text-xs font-bold">⇆</span>
      </div>
    </motion.div>
  );
};
